import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { LineKeysTypes } from '@frontend/api-line-keys';
import { styles, Text } from '@frontend/design-system';
import { useLineKeyState } from '../store/line-key-provider';

export const DeviceLayoutHandheld = forwardRef<
  HTMLDivElement,
  {
    isPreviewEnabled?: boolean;
    lineKeys: LineKeysTypes.GetLineKeysType['output']['lineKeys'] | undefined;
    model: LineKeysTypes.ModelInfoType | undefined;
    pageNum: number;
  }
>(({ isPreviewEnabled, lineKeys, model, pageNum }, ref) => {
  let { scrollToCard } = useLineKeyState(['scrollToCard']);
  if (isPreviewEnabled) {
    scrollToCard = () => {};
  }

  // Handheld devices display 4 line keys per page in a 1x4 grid (vertical)
  const columns = 1;
  const rows = 4;
  const itemsPerPage = columns * rows;
  const currentPageIndex = pageNum - 1;
  const startIndex = currentPageIndex * itemsPerPage;
  const maxHandheldLineKeys = model?.handheldKeys?.maxKeys ?? lineKeys?.length ?? 0;
  const cappedLineKeys = (lineKeys ?? []).slice(0, maxHandheldLineKeys);
  const paginatedLineKeys = cappedLineKeys.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div ref={ref} className='handheld-container' css={[gridStyles({ columns, rows }), handheldStyles()]}>
      {paginatedLineKeys.map((lineKey, index) => {
        const keyNumber = startIndex + index + 1;

        return (
          <div
            key={`key-${keyNumber}`}
            className='handheld-line-key-tile active'
            css={gridItemStyles({ position: undefined })}
            onClick={() => {
              if (!isPreviewEnabled) {
                scrollToCard(keyNumber);
              }
            }}
          >
            <Text weight='bold' size='medium' className='handheld-line-key-tile-key' as='span'>
              {keyNumber}
            </Text>
            <Text weight='bold' size='medium' className='handheld-line-key-tile-name' css={styles.truncate} as='span'>
              {lineKey.name}
            </Text>
          </div>
        );
      })}
    </div>
  );
});

DeviceLayoutHandheld.displayName = 'DeviceLayoutHandheld';

const gridStyles = ({ columns, rows }: { columns: number; rows: number }) => css`
  display: grid;
  grid-template-columns: repeat(${columns}, 1fr);
  grid-template-rows: repeat(${rows}, 97px);
  gap: var(--fabric-space-gap-xxs);
`;

const gridItemStyles = ({ position }: { position?: { column: number; row: number } }) => {
  return css`
    grid-column: ${position?.column};
    grid-row: ${position?.row};
  `;
};

const handheldStyles = () => css`
  background: var(--fabric-color-neutral-100);
  padding: var(--fabric-space-padding-xl) var(--fabric-space-padding-lg);
  border-radius: 16px;

  .handheld-line-key-tile {
    border: 2px solid var(--fabric-color-neutral-30);
    border-radius: var(--fabric-radius-md);
    background: var(--fabric-color-neutral-00);
    padding: var(--fabric-space-padding-xs);
    display: flex;
    gap: var(--fabric-space-gap-xs);
  }

  .handheld-line-key-tile-key {
    color: var(--fabric-color-neutral-50);
  }

  .handheld-line-key-tile-name {
    color: var(--fabric-color-neutral-90);
  }

  .handheld-line-key-tile.static {
    border: 2px solid var(--fabric-color-neutral-20);
  }

  .handheld-line-key-tile.active:not(.static, .preview):hover {
    background: var(--fabric-color-neutral-5);
    color: var(--fabric-color-neutral-50);
    cursor: pointer;
  }

  .handheld-line-key-tile.active:not(.static, .preview):active {
    background: var(--fabric-color-neutral-10);
    color: var(--fabric-color-neutral-50);
  }

  .handheld-line-key-tile.static .handheld-line-key-tile-name {
    color: var(--fabric-color-neutral-30);
  }

  .handheld-line-key-tile.inactive {
    border-color: transparent;
  }

  .handheld-line-key-tile.inactive .handheld-line-key-tile-key {
    color: var(--fabric-color-neutral-20);
  }
`;
